import { store, Unsubscribe } from "@stencil/redux";
import { forceUpdate } from "@stencil/core";
import { ConversationTypeEnum } from "@auvious/integrations";
import {
  Component,
  ComponentInterface,
  h,
  State,
  Event,
  EventEmitter,
  Prop,
  Watch,
  Element,
  Method,
} from "@stencil/core";

import {
  CobrowseStateEnum,
  ConversationStateEnum,
  IThemeOptions,
  WidgetsEnum,
} from "../../models";
import { RootState } from "../../store/states";
import {
  AppConfigService,
  ErrorHandler,
  ThemeService,
  TranslateService,
} from "../../services";
import { AVBusService } from "../../services/bus.service";
import { NotificationService } from "../../services/notification.service";
import { dispatchCobrowseRequestTypeChange } from "../../store/actions/cobrowse";
import { Subscription } from "rxjs";

interface ILauncherState {
  isMenuOpen?: boolean;
  isConfirmOpen?: boolean;
  activeAction?: ConversationTypeEnum;
}

const DEFAULT_LAUNCHER_STATE: ILauncherState = {
  isMenuOpen: false,
  isConfirmOpen: false,
  activeAction: null,
};

@Component({
  tag: "app-launcher",
  styleUrl: "app-launcher.scss",
  shadow: true,
})
export class AppLauncher implements ComponentInterface {
  @Prop() skipConfirm: boolean = false;
  @Prop() loading: boolean = false;

  @Prop({ mutable: true }) $conversationType: ConversationTypeEnum;
  @Prop({ mutable: true }) $conversationState: ConversationStateEnum;
  @Prop({ mutable: true }) $cobrowseState: CobrowseStateEnum;

  @Watch("$conversationType")
  launchAction(action: ConversationTypeEnum) {
    if (action == this.state.activeAction) {
      return;
    }
    this.state = { ...this.state, activeAction: action, isMenuOpen: false };
  }

  @Watch("$cobrowseState")
  cobrowseStateChange(newState: CobrowseStateEnum) {
    if (
      newState === CobrowseStateEnum.idle &&
      this.state.activeAction === ConversationTypeEnum.cobrowse
    ) {
      // cobrowse ended remotely
      this.state = { ...this.state, activeAction: null, isConfirmOpen: false };
    }
  }

  @Prop() widgets: WidgetsEnum[];

  @State() state: ILauncherState;

  @Event() actionSelected: EventEmitter<ConversationTypeEnum>;
  @Event() closeRequest: EventEmitter<void>;
  @Event() toggled: EventEmitter<boolean>;

  @Element() host: HTMLElement;

  @Method()
  async focusAction(action: ConversationTypeEnum) {
    switch (action) {
      case ConversationTypeEnum.close:
        this.closeButton?.setFocus();
        break;
    }
  }

  dispatchCobrowseRequestTypeChange: typeof dispatchCobrowseRequestTypeChange;

  storeUnsubscribe: Unsubscribe;
  themeOptions: IThemeOptions;
  totemButtons: HTMLAvButtonElement[] = [];
  closeButton: HTMLAvButtonElement;
  subscriptions = new Subscription();

  constructor() {
    this.state = { ...DEFAULT_LAUNCHER_STATE };
    ThemeService.optionsAvailable().subscribe((o) => (this.themeOptions = o));
  }

  componentWillLoad(): Promise<void> | void {
    this.subscriptions.add(
      TranslateService.langChange$.subscribe(() => forceUpdate(this))
    );
  }

  connectedCallback() {
    if (this.storeUnsubscribe) this.storeUnsubscribe();

    this.storeUnsubscribe = store.mapStateToProps(
      this,
      (rootState: RootState) => {
        const {
          conversation: { type, state },
        } = rootState;
        const cobrowseState = rootState.cobrowse.state;
        return {
          $conversationType: type,
          $conversationState: state,
          $cobrowseState: cobrowseState,
        };
      }
    );

    store.mapDispatchToProps(this, {
      dispatchCobrowseRequestTypeChange,
    });
  }

  disconnectedCallback() {
    this.storeUnsubscribe();
    this.subscriptions.unsubscribe();
  }

  componentDidLoad() {
    AVBusService.$terminate.subscribe((confirm) => this.close(confirm));
  }

  registerButtonRef(el) {
    if (!this.totemButtons.includes(el)) {
      this.totemButtons.push(el);
    }
  }

  toggleMenu() {
    if (this.loading) {
      return;
    }
    if (!this.state.isMenuOpen) {
      if (this.isSingleWidget) {
        switch (this.widgets[0]) {
          case WidgetsEnum.video:
            this.action(ConversationTypeEnum.videoCall);
            break;
          case WidgetsEnum.callback:
            this.action(ConversationTypeEnum.callback);
            break;
          case WidgetsEnum.audio:
            this.action(ConversationTypeEnum.voiceCall);
            break;
          case WidgetsEnum.chat:
            this.action(ConversationTypeEnum.chat);
            break;
          case WidgetsEnum.cobrowse:
            this.dispatchCobrowseRequestTypeChange("view");
            this.action(ConversationTypeEnum.cobrowse);
            break;
          case WidgetsEnum.screenshare:
            this.dispatchCobrowseRequestTypeChange("display-capture");
            this.action(ConversationTypeEnum.cobrowse);
            break;
        }
      } else {
        this.state = { ...this.state, isMenuOpen: !this.state.isMenuOpen };
        this.toggled.emit(this.state.isMenuOpen);
      }
    } else {
      this.state = { ...this.state, isMenuOpen: !this.state.isMenuOpen };
      this.toggled.emit(this.state.isMenuOpen);
    }

    // focus first item on list
    requestAnimationFrame(() => {
      if (this.state.isMenuOpen && this.totemButtons.length > 0) {
        this.totemButtons[0].setFocus();
      }
    });
  }

  get interacted() {
    return this.state.activeAction !== null;
  }

  action(action: ConversationTypeEnum) {
    this.state = { ...this.state, activeAction: action, isMenuOpen: false };
    this.actionSelected.emit(action);
    this.toggled.emit(false);
  }

  close(confirm = true) {
    ErrorHandler.addBreadcrumb("Widget action close");
    if (this.$cobrowseState === CobrowseStateEnum.connecting) {
      return;
    }
    if (
      this.state.activeAction === ConversationTypeEnum.callback ||
      this.skipConfirm ||
      !confirm ||
      (this.state.activeAction === ConversationTypeEnum.cobrowse &&
        this.$cobrowseState === CobrowseStateEnum.idle)
    ) {
      this.closeComplete();
    } else {
      NotificationService.confirm(TranslateService.instant("Are you sure?"), {
        onApprove: this.closeComplete.bind(this),
        onReject: () => {
          this.focusAction(ConversationTypeEnum.close);
        },
      });
    }
  }

  closeComplete() {
    this.state = { ...this.state, activeAction: null };
    this.closeRequest.emit();
  }

  get isVideoEnabled() {
    return this.widgets?.includes(WidgetsEnum.video);
  }
  get isChatEnabled() {
    return this.widgets?.includes(WidgetsEnum.chat);
  }
  get isDisplayCaptureEnabled() {
    return this.widgets?.includes(WidgetsEnum.screenshare);
  }
  get isCallbackEnabled() {
    return this.widgets?.includes(WidgetsEnum.callback);
  }
  get isAudioEnabled() {
    return this.widgets?.includes(WidgetsEnum.audio);
  }
  get isCobrowseEnabled() {
    return this.widgets?.includes(WidgetsEnum.cobrowse);
  }
  get isSingleWidget() {
    return this.widgets?.length === 1;
  }
  get isDisconnected() {
    return this.$conversationState === ConversationStateEnum.disconnected;
  }
  get isPositionLeft() {
    return this.themeOptions.positionHorizontal === "left";
  }
  get isPositionRight() {
    return (
      this.themeOptions.positionHorizontal === "right" ||
      this.themeOptions.positionHorizontal === "center"
    );
  }
  get isPositionTop() {
    return this.themeOptions.positionVertical === "top";
  }
  get isPositionBottom() {
    return this.themeOptions.positionVertical === "bottom";
  }
  get isCloseVisible() {
    return !AppConfigService.closeHidden;
  }

  render() {
    const videoAriaLabel = TranslateService.instant("Video call support");
    const audioAriaLabel = TranslateService.instant("Voice call support");
    const callbackAriaLabel = TranslateService.instant(
      "Schedule a support video call"
    );
    const cobrowseAriaLabel = TranslateService.instant("Co-browse support");
    const displayCaptureAriaLabel = TranslateService.instant(
      "Screen share support"
    );
    const multipleWidgetsOpenAriaLabel =
      TranslateService.instant("Contact support");
    const multipleWidgetsCloseAriaLabel = TranslateService.instant(
      "Close contact support menu"
    );
    const chatAriaLabel = TranslateService.instant("Chat support");

    const background = AppConfigService.launcherImageUrl;
    let singleWidgetAriaLabel;
    if (this.isSingleWidget) {
      switch (this.widgets[0]) {
        case WidgetsEnum.audio:
          singleWidgetAriaLabel = audioAriaLabel;
          break;
        case WidgetsEnum.video:
          singleWidgetAriaLabel = videoAriaLabel;
          break;
        case WidgetsEnum.callback:
          singleWidgetAriaLabel = callbackAriaLabel;
          break;
        case WidgetsEnum.chat:
          singleWidgetAriaLabel = chatAriaLabel;
          break;
        case WidgetsEnum.cobrowse:
          singleWidgetAriaLabel = cobrowseAriaLabel;
          break;
        case WidgetsEnum.screenshare:
          singleWidgetAriaLabel = displayCaptureAriaLabel;
      }
    } else {
      singleWidgetAriaLabel = this.state.isMenuOpen
        ? multipleWidgetsCloseAriaLabel
        : multipleWidgetsOpenAriaLabel;
    }

    return (
      <div
        class={{
          "av-launcher": true,
          "av-launcher-open": this.state.isMenuOpen,
          "av-launcher-disabled": this.isDisconnected,
          "av-launcher-top": this.isPositionTop,
          "av-launcher-bottom": this.isPositionBottom,
          "av-launcher-left": this.isPositionLeft,
          "av-launcher-right": this.isPositionRight,
        }}
      >
        {this.isVideoEnabled && (
          <div
            data-tid="launch/video"
            class="av-launcher-item"
            onClick={() => this.action(ConversationTypeEnum.videoCall)}
          >
            {this.isPositionRight && (
              <label aria-hidden="true">
                <av-translate>Video call</av-translate>
              </label>
            )}
            <av-button
              ref={this.registerButtonRef.bind(this)}
              ariaLabel={videoAriaLabel}
              color="primary"
              shape="round"
            >
              <av-asset image="cam"></av-asset>
            </av-button>
            {this.isPositionLeft && (
              <label aria-hidden="true">
                <av-translate>Video call</av-translate>
              </label>
            )}
          </div>
        )}

        {this.isAudioEnabled && (
          <div
            data-tid="launch/audio"
            class="av-launcher-item"
            onClick={() => this.action(ConversationTypeEnum.voiceCall)}
          >
            {this.isPositionRight && (
              <label aria-hidden="true">
                <av-translate>Voice call</av-translate>
              </label>
            )}
            <av-button
              ref={this.registerButtonRef.bind(this)}
              ariaLabel={audioAriaLabel}
              color="primary"
              shape="round"
            >
              <av-asset image="mic"></av-asset>
            </av-button>
            {this.isPositionLeft && (
              <label aria-hidden="true">
                <av-translate>Voice call</av-translate>
              </label>
            )}
          </div>
        )}

        {this.isCallbackEnabled && (
          <div
            data-tid="launch/schedule"
            class="av-launcher-item"
            onClick={() => this.action(ConversationTypeEnum.callback)}
          >
            {this.isPositionRight && (
              <label aria-hidden="true">
                <av-translate>Schedule Call</av-translate>
              </label>
            )}
            <av-button
              ref={this.registerButtonRef.bind(this)}
              ariaLabel={callbackAriaLabel}
              color="primary"
              shape="round"
            >
              <av-asset image="calendar-time"></av-asset>
            </av-button>
            {this.isPositionLeft && (
              <label aria-hidden="true">
                <av-translate>Schedule Call</av-translate>
              </label>
            )}
          </div>
        )}

        {this.isDisplayCaptureEnabled && (
          <div
            data-tid="launch/display-capture"
            class="av-launcher-item"
            onClick={() => {
              this.dispatchCobrowseRequestTypeChange("display-capture");
              this.action(ConversationTypeEnum.cobrowse);
            }}
          >
            {this.isPositionRight && (
              <label aria-hidden="true">
                <av-translate>Share screen</av-translate>
              </label>
            )}
            <av-button
              ref={this.registerButtonRef.bind(this)}
              ariaLabel={displayCaptureAriaLabel}
              color="primary"
              shape="round"
            >
              <av-asset image="cobrowse-screen"></av-asset>
            </av-button>
            {this.isPositionLeft && (
              <label aria-hidden="true">
                <av-translate>Share screen</av-translate>
              </label>
            )}
          </div>
        )}

        {this.isCobrowseEnabled && (
          <div
            data-tid="launch/cobrowse"
            class="av-launcher-item"
            onClick={() => {
              this.dispatchCobrowseRequestTypeChange("view");
              this.action(ConversationTypeEnum.cobrowse);
            }}
          >
            {this.isPositionRight && (
              <label aria-hidden="true">
                <av-translate>Co-browse</av-translate>
              </label>
            )}
            <av-button
              ref={this.registerButtonRef.bind(this)}
              ariaLabel={cobrowseAriaLabel}
              color="primary"
              shape="round"
            >
              <av-asset image="cobrowse"></av-asset>
            </av-button>
            {this.isPositionLeft && (
              <label aria-hidden="true">
                <av-translate>Co-browse</av-translate>
              </label>
            )}
          </div>
        )}

        {this.isChatEnabled && (
          <div
            data-tid="launch/chat"
            class="av-launcher-item"
            onClick={() => this.action(ConversationTypeEnum.chat)}
          >
            {this.isPositionRight && (
              <label aria-hidden="true">
                <av-translate>Chat</av-translate>
              </label>
            )}
            <av-button
              ref={this.registerButtonRef.bind(this)}
              ariaLabel={chatAriaLabel}
              color="primary"
              shape="round"
            >
              <av-asset image="chat"></av-asset>
            </av-button>
            {this.isPositionLeft && (
              <label aria-hidden="true">
                <av-translate>Chat</av-translate>
              </label>
            )}
          </div>
        )}

        {this.loading && (
          <av-button color="primary" shape="round">
            <av-spinner></av-spinner>
          </av-button>
        )}
        {!this.loading && this.interacted && this.isCloseVisible && (
          <av-button
            ariaLabel={multipleWidgetsCloseAriaLabel}
            data-tid="launch/close"
            disabled={this.isDisconnected}
            color="primary"
            shape="round"
            ref={(e) => (this.closeButton = e)}
            onClick={() => this.close()}
          >
            <av-asset image="close"></av-asset>
          </av-button>
        )}
        {!this.loading && !this.interacted && (
          <av-button
            ariaLabel={singleWidgetAriaLabel}
            data-tid="launch/toggle-menu"
            background={!!background && !this.state.isMenuOpen}
            onClick={this.toggleMenu.bind(this)}
            color="primary"
            shape="round"
          >
            {!this.state.isMenuOpen && [
              !this.isSingleWidget && [
                !!background ? (
                  <img class="av-image-bkg" src={background} />
                ) : (
                  <av-asset image="headset"></av-asset>
                ),
              ],
              this.isSingleWidget &&
                (!!background
                  ? [<img class="av-image-bkg" src={background} />]
                  : [
                      this.isVideoEnabled && <av-asset image="cam"></av-asset>,
                      this.isCallbackEnabled && (
                        <av-asset image="calendar-time"></av-asset>
                      ),
                      this.isAudioEnabled && <av-asset image="mic"></av-asset>,
                      this.isChatEnabled && <av-asset image="chat"></av-asset>,
                      this.isDisplayCaptureEnabled && (
                        <av-asset image="cobrowse-screen"></av-asset>
                      ),
                      this.isCobrowseEnabled && (
                        <av-asset image="cobrowse"></av-asset>
                      ),
                    ]),
            ]}
            {this.state.isMenuOpen && (
              <div
                class={{
                  "av-arrow": true,
                  "av-arrow-down": this.isPositionBottom,
                  "av-arrow-up": this.isPositionTop,
                }}
              ></div>
            )}
          </av-button>
        )}
      </div>
    );
  }
}
